<script lang="ts">
  import {environment} from '$lib/env';
  import {user, userReducer, userToken} from '$lib/stores';
  import {goto} from '$app/navigation';
  import {serialize} from 'cookie';
  import {closeDialog, openDialog} from "$lib/stores/dialogStores";
  import Button from "$lib/elements/Button.svelte";
  import PasswordIcon from "$lib/icons/PasswordIcon.svelte";
  import EmailIcon from "$lib/icons/EmailIcon.svelte";
  import EyeIcon from "$lib/icons/EyeIcon.svelte";
  import EyeOffIcon from "$lib/icons/EyeOffIcon.svelte";
  import {locale, translate} from "$lib/i18n";
  import {toasts} from "$lib/stores/toastStores";
  import CodeInput from "$lib/elements/CodeInput.svelte";
  import {deleteCookie, getCookie, setCookie} from "$lib/utils/cookies";

  let error: string | null = $state(null);
  let username = $state('');
  let password = $state('');
  let totpCode = '';
  let loading = $state(false);
  let isValid = false;
  let passwordVisible = $state(false);
  let passwordInput: HTMLInputElement = $state();
  let totpChallenge = $state(false);

  const handleSubmit = async (event: Event) : Promise<void> => {
    event.preventDefault();
    loading = true;
    error = null;
    let dataForm = {
      username,
      password,
      totpCode
    }

    try {
      const response = await fetch(`${environment.apiUrl}/auth`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(dataForm)
      });

      if (!response.ok) {
        const { message } = await response.json();
        throw new Error(message || 'Login failed');
      }

      const { data } = await response.json();

      if (data.totpChallenge) {
        totpChallenge = data.totpChallenge;
        loading = false;
        return;
      }

      if (getCookie('forza_token')) {
        deleteCookie('forza_token')
      }

      setCookie('forza_token', data.tokens.accessToken, 30)

      userReducer(data.user);
      closeDialog('LoginModal');
      goto(`/${$locale}/casino`, {replaceState: true, noScroll: true}).then(() => {
        userToken.set('Bearer ' + data.tokens.accessToken);
      });
    } catch (err: any) {
      loading = false;
      error = err.message || 'An error occurred. Please try again.';
    }
  }

  function handlePasswordVisibility() {
    passwordVisible = !passwordVisible;
    passwordInput.type = passwordVisible ? 'text' : 'password';
  }

  function openForgotPasswordModal(name: string) {
    openDialog(name);
  }

  function handleOTPChange(event: any) {
    totpCode = event.detail.otpCode;
    isValid = event.detail.isValid;
  }
</script>

<form class="w-full max-w-md px-4" on:submit={handleSubmit}>
  <div class="flex flex-col gap-5">
    {#if !totpChallenge}
      <div class="text-center">
        <h2 class="text-2xl font-bold mb-1 text-white">{$translate('Welcome Back')}</h2>
        <p class="text-sm text-gray-400">Sign in to continue to your account</p>
      </div>

      <div class="space-y-4">
        <div class="relative">
          <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-3 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
            <EmailIcon />
            <input
                    class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                    type="text"
                    id="username"
                    placeholder="Username"
                    bind:value={username}
                    required
            >
          </div>
        </div>

        <div class="relative">
          <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-3 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
            <PasswordIcon />
            <input
                    class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                    type="password"
                    bind:this={passwordInput}
                    id="password"
                    placeholder="Password"
                    bind:value={password}
                    required
            >
            <button
                    type="button"
                    class="focus:outline-none text-gray-400 hover:text-white transition-colors"
                    on:click={() => handlePasswordVisibility()}
            >
              {#if passwordVisible}
                <EyeIcon />
              {:else}
                <EyeOffIcon />
              {/if}
            </button>
          </div>
        </div>
      </div>
    {:else}
      <div class="text-center">
        <h2 class="text-2xl font-bold mb-1 text-white">{$translate('Two-Factor Authentication')}</h2>
        <p class="text-sm text-gray-400 mb-4">Enter the 6-digit code from your authenticator app</p>
      </div>

      <div class="flex justify-center my-6">
        <CodeInput on:change={handleOTPChange} />
      </div>
    {/if}
  </div>

  {#if error}
    <div class="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm flex items-start">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <span>{error}</span>
    </div>
  {/if}

  <div class="mt-6">
    <Button
            loading={loading}
            type="submit"
            clas="w-full py-3 text-base font-medium"
    >
      {$translate('Sign In')}
    </Button>
  </div>

  <div class="mt-4 text-center">
    <button
            on:click={() => openForgotPasswordModal('ForgotPasswordModal')}
            type="button"
            class="text-[#FF3E00] hover:text-[#FF5722] text-sm font-medium transition-colors"
    >
      {$translate('Forgot password?')}
    </button>
  </div>
</form>

<style>
  form {
    display: flex;
    flex-direction: column;
  }
</style>